import { useState } from "react";
import { Plus, ScrollText, Pencil, Trash2 } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import Loading from "@/components/dashboard/Loading";
import { useDashboard, useDashboardActions } from "@/hooks/useDashboard";
import { toast } from "sonner";

const TYPES = ["Limit", "Whitelist", "Blocklist", "Approval", "Budget"];

const typeColor = {
  Limit: "text-[#4F8CFF] bg-[#4F8CFF]/10 border-[#4F8CFF]/20",
  Whitelist: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  Blocklist: "text-red-400 bg-red-500/10 border-red-500/20",
  Approval: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  Budget: "text-[#7C5CFF] bg-[#7C5CFF]/10 border-[#7C5CFF]/20",
};

const EMPTY = { name: "", scope: "All agents", value: "", type: "Limit", enabled: true };

export default function Policies() {
  const { data, isLoading } = useDashboard();
  const { togglePolicy, createPolicy, updatePolicy, deletePolicy } = useDashboardActions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  if (isLoading || !data) {
    return (<div><PageHeader title="Policies" subtitle="Rules Vvvoraa enforces before any agent can spend." /><Loading /></div>);
  }

  const toggle = async (id) => {
    await togglePolicy(id);
    toast.success("Policy updated");
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, scope: p.scope, value: p.value, type: p.type, enabled: p.enabled });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.value.trim()) { toast.error("Name and value are required"); return; }
    setSaving(true);
    const body = {
      name: form.name.trim(),
      scope: form.scope.trim() || "All agents",
      value: form.value.trim(),
      type: form.type,
      enabled: form.enabled,
    };
    try {
      if (editing) { await updatePolicy(editing.id, body); toast.success("Policy updated"); }
      else { await createPolicy(body); toast.success("Policy created"); }
      setDialogOpen(false);
    } catch { toast.error("Could not save policy"); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await deletePolicy(toDelete.id); toast.success("Policy deleted"); }
    catch { toast.error("Could not delete policy"); }
    finally { setToDelete(null); }
  };

  return (
    <div>
      <PageHeader title="Policies" subtitle="Rules Vvvoraa enforces before any agent can spend.">
        <Button onClick={openCreate} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="add-policy-btn">
          <Plus className="h-4 w-4" /> New policy
        </Button>
      </PageHeader>

      <div className="space-y-3">
        {data.policies.map((p) => (
          <Panel key={p.id} className="p-5 flex items-center justify-between gap-4" data-testid={`policy-row-${p.id}`}>
            <div className="flex items-start gap-4 min-w-0">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 text-slate-300 shrink-0"><ScrollText className="h-5 w-5" /></span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-semibold text-white">{p.name}</p>
                  <span className={`text-[10px] font-mono uppercase tracking-wider rounded-full border px-2 py-0.5 ${typeColor[p.type] || typeColor.Limit}`}>{p.type}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{p.scope} · <span className="text-slate-300">{p.value}</span></p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors" data-testid={`policy-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => setToDelete(p)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`policy-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              <Switch checked={p.enabled} onCheckedChange={() => toggle(p.id)} className="data-[state=checked]:bg-[#4F8CFF]" data-testid={`policy-toggle-${p.id}`} />
            </div>
          </Panel>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0A1119] border-white/10 text-white" data-testid="policy-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit policy" : "New policy"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="text-slate-300">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Per-transaction cap" className="bg-white/[0.03] border-white/12 text-white" data-testid="policy-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Scope</Label>
                <Input value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} placeholder="All agents" className="bg-white/[0.03] border-white/12 text-white" data-testid="policy-form-scope" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="bg-white/[0.03] border-white/12 text-white" data-testid="policy-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A1119] border-white/10 text-white">
                    {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-300 text-xs">Value</Label>
              <Input value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} placeholder="e.g. $500 or AWS, GCP" className="bg-white/[0.03] border-white/12 text-white" data-testid="policy-form-value" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
              <span className="text-sm text-slate-300">Enabled</span>
              <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} className="data-[state=checked]:bg-[#4F8CFF]" data-testid="policy-form-enabled" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="policy-form-save">{saving ? "Saving…" : editing ? "Save changes" : "Create policy"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-[#0A1119] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete policy?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This permanently removes "{toDelete?.name}" and cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="policy-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
